/**
 * SystemGovernancePage 系統與權限 (S10.1) — evolved from the codex GovernancePage.
 *
 * Board D #10 permissions surface. Tabs are ROUTE-DERIVED
 * (`/auth/governance/:tab`, tab = organisations|roles|hierarchy|users|audit) —
 * selection lives in the URL, never component state (house selection tenet;
 * PriceSetupPage / ApPage precedent). Route prefix stays `/auth/governance` so
 * the existing nav item and the routePermissions `governance.manage` gate keep
 * working unchanged.
 *
 *  - 組織 OrganisationsTab: 母/子公司 + 直營/加盟, CRUD-lite.
 *  - 角色 RolesTab: 業務/採購/財務 presets + editable role-permission matrix.
 *  - 角色階層 RoleHierarchyPanel (S2.3, behind `governance.rbacV2`): roles
 *    grouped by seniority band + per-role `inheritsFrom` editing + level
 *    manager + the clone-preset create-role modal. Flag OFF ⇒ the tab is not
 *    registered at all, `/auth/governance/hierarchy` redirects to organisations,
 *    and the page renders byte-identical to today (zero visible change).
 *  - 用戶 UsersTab: activate/deactivate, role assign, and「以此身分檢視」to
 *    exercise ProtectedRoute's deny path with the narrow-role user.
 *  - 稽核 AuditLogTab: the global review lens (date+actor+module filters, drill,
 *    CSV export) fed by every ReasonRequiredModal write + live price overrides.
 */

import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import PageContainer from '@/components/common/PageContainer';
import PageHeader from '@/components/common/PageHeader';
import { useFeature } from '@/features/flags';
import OrganisationsTab from './OrganisationsTab';
import RolesTab from './RolesTab';
import RoleHierarchyPanel from './RoleHierarchyPanel';
import UsersTab from './UsersTab';
import AuditLogTab from './AuditLogTab';

export type SystemTab = 'organisations' | 'roles' | 'hierarchy' | 'users' | 'audit';

const BASE_TABS: readonly SystemTab[] = ['organisations', 'roles', 'users', 'audit'] as const;

/** rbacV2 tab set — the hierarchy surface slots in beside the matrix (S2.3). */
const RBAC_V2_TABS: readonly SystemTab[] = [
  'organisations',
  'roles',
  'hierarchy',
  'users',
  'audit',
] as const;

const SystemGovernancePage = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab: string }>();
  const { t } = useTranslation('governance');
  const rbacV2 = useFeature('governance.rbacV2');

  const systemTabs = rbacV2 ? RBAC_V2_TABS : BASE_TABS;
  const tab = systemTabs.find((candidate) => candidate === tabParam) ?? null;
  if (!tab) {
    return <Navigate to="/auth/governance/organisations" replace />;
  }

  return (
    <PageContainer>
      <PageHeader title={t('title')} />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(`/auth/governance/${key}`)}
        items={systemTabs.map((key) => ({ key, label: t(`tabs.${key}`) }))}
      />
      {tab === 'organisations' && <OrganisationsTab />}
      {tab === 'roles' && <RolesTab />}
      {tab === 'hierarchy' && <RoleHierarchyPanel />}
      {tab === 'users' && <UsersTab />}
      {tab === 'audit' && <AuditLogTab />}
    </PageContainer>
  );
};

export default SystemGovernancePage;
